import type { BaseTranslation } from '../../i18n-types';

const shared: BaseTranslation = {
  accessGuard: {
    confirmText: 'Gain access',
    error: {
      gainAccess: 'Error gaining access',
    },
    inputPlaceholder: 'Why do you need access to this patient?',
    message:
      'You do not have access to this patient. You can gain temporary access for 24 hours.',
    rejectText: 'Go back',
    title: 'You do not have access',
  },
  forbiddenPatient: {
    title: "We can't confirm your access to this patient",
    body: "Do you work at more than one clinic? Make sure you're logged in at the right one.",
    nspVerification:
      "Your access depends on a treatment relationship, which we verify through the national systems. We can't find one right now.",
    contactSupport:
      "Still need access? Reach out to our support - we're happy to help.",
    supportPhoneLabel: 'Tel:',
    contactSupportButton: 'Contact support',
    goBack: 'Go back',
  },
  alertMessages: {
    anErrorOccurred: 'An error occurred',
    cantBeUndone: 'This action cannot be undone.',
    comingSoon: 'Coming soon!',
    confirmDelete: 'Are you sure you want to delete this?',
    confirmDeleteLesion:
      'Are you sure you want to delete this lesion? This action cannot be undone.',
    confirmDeleteRash:
      'Are you sure you want to delete this rash? This action cannot be undone.',
    confirmDeleteDraft:
      'Are you sure you want to delete this draft? Previous entries on this case will be kept.',
    deleteLesion: 'Delete lesion?',
    deleteRash: 'Delete rash?',
    deleteDraft: 'Delete draft?',
    deletionError: 'Deletion failed',
    deletionSuccess: 'Successfully deleted',
    imageNotSaved: `{imagetype} image not saved to due to an error. Please add new image if needed.`,
    theLesionIsEmpty: 'The lesion is empty',
    theRashIsEmpty: 'The rash is empty',
    wouldYouLikeToDelete: 'Would you like to delete it?',
  },
  assessmentTypes: {
    histopathologicalEvaluation: 'Histopathological assessment',
    teleEvaluation: 'Teledermatological assessment',
  },
  authentication: {
    mitid: {
      login: 'Login with MitID',
      newUser: 'New user',
      notYet: 'Not yet',
      title: 'MitId in Dermloop',
      errorTitle: 'Something went wrong',
      signinWithOtherAcc: 'Sign in with another MitID',
      wrongMitId:
        'You have logged in with a wrong MitID and it does not match the cvr number from your site',
      subtitle: 'We are in the midst of implementing MitID in Dermloop',
      oneLoginSubtitle: 'You are now logged in via your EHR system.',
      oneLoginConnectAccounts:
        'To continue, you now need to connect using the MitID Erhverv associated with the clinic where you work. This step ensures we can link your account correctly.',
      connectAccounts:
        'Which is why we kindly ask you to connect your Dermloop account to MitID. We appologize for the inconvenience.',
      notYetConnected:
        'You have not yet connected your Dermloop account to MitID. If your site has MitID enabled please login like normally and follow the migration steps. Otherwise contact support.',
      goBack: 'Go back',
      migrate: 'Migrate to MitID',
      migratingUser: 'Migrating {user:string} to this Dermloop account',
    },
    desktopQrLogin: {
      errorMessage:
        'There was a problem logging in. If the issue persists, contact support: +1 (833) 690-2903',
      alreadyRedeemedErrorMessage:
        "This code was already used to log in. If this wasn't you, please contact support: +1 (833) 690-2903",
    },
    errors: {
      fetchingUser: 'Error occured while fetching user',
      internalServer: 'Internal server error. Please try again later.',
      websocketConnection: 'No connection to websocket server',
    },
    inputs: {
      email: {
        label: 'Email',
      },
      firstName: {
        label: 'First name',
      },
      lastName: {
        label: 'Last name',
      },
      missingValueMessage: 'Please enter your {label:string}',
      password: {
        label: 'Password',
      },
      phonenumber: {
        label: 'Phone',
        validation: {
          invalidForPrefix: 'Phone number does not match country code',
        },
      },
      termsAndConditions: {
        label: 'Email',
      },
    },
    screens: {
      invalidLoginMethod: {
        title: 'Invalid login method',
        description:
          'The login method you used is not valid for this account. Please use the correct login method associated with your account.',
        button: 'Go back to login',
      },
      approveDeviceSession: {
        description:
          'Approve access to device by pressing the letters that matches the letters on the device your are authenticating.',
        errorResult: {
          text: 'Error occured while authenticating device session. Please scan the QR code again.',
          title: 'Device not authenticated',
        },
        errors: {
          gettingDeviceSession: 'Error occured while getting device session',
          missingAuthCode: 'No code was provided in URL parameters',
          missingSessionId: 'No sessionId was provided in URL parameters',
        },
        successResult: {
          text: 'You can now close this window.',
          title: 'Device authenticated',
        },
        title: 'Authenticate device',
      },
      changePassword: {
        inputs: {
          confirmNewPassword: {
            label: 'Confirm new password',
            validation: {
              passwordMismatch: 'Passwords do not match',
            },
          },
          newPassword: {
            label: 'New password',
          },
        },
        reasons: {
          newLoginSystem:
            'Dermloop is migrating to a new login system. Due to this we kindly ask you to set a new password.',
        },
        submitText: 'Change password',
        successResult: {
          recoveryModeOnlyText: 'You can now close this screen',
          text: 'You can now access the app by clicking on the button below.',
          title: 'Your password has been successfully changed.',
        },
        title: 'Change password',
        titleSucess: 'Password changed successfully',
      },
      completeProfile: {
        title: 'Complete your profile',
        firstName: 'First name',
        firstNamePlaceholder: 'John',
        lastName: 'Last name',
        lastNamePlaceholder: 'Doe',
        email: 'Email - So we can contact you',
        emailPlaceholder: 'example@domain.com',
        continueButton: 'Continue',
        helpUsText:
          'Thank you for helping us filling out your missing information',
        errors: {
          thisIsRequired: 'This is required',
          errorUpdatingUser: 'Something went wrong while updating the user.',
        },
      },
      createAccount: {
        existingAccountLinkText: 'Log in',
        existingAccountText: 'Already have an account?',
        privacyPolicyLink: 'https://melatech.io/legal/privacy',
        privacyPolicyLinkText: 'Privacy policy.',
        readTermsAndConditionsText: 'I have read and agree to the',
        submitText: 'Sign up',
        subtitle:
          'Create an account to access our library of cases and much more.',
        termsAndConditionsLink: 'https://melatech.io/legal/terms',
        termsAndConditionsLinkText: 'Terms & conditions',
        termsAndConditionsSeperator: 'and',
        title: 'Create account',
      },
      login: {
        createAccount: 'Create account',
        creatingQRCode: 'Creating QR code',
        dermKit: 'Dermkit',
        errors: {
          errorOccuredWhileCreatingQRCode:
            'Error occured while creating QR code',
          incorrectEmailOrPassword: 'Incorrect email or password.',
          qrCodeExpired: 'QR code expired',
        },
        forgotPassword: 'Forgot password?',
        login: 'Log in',
        loginError: 'Sign-in failed',
        loginWithEmail: 'Switch to email',
        loginWithGoogle: 'Log in with Google',
        loginWithQRCode: 'Log in with QR code',
        needAnAccount: 'New to Dermloop?',
        personalDevice: 'Personal Device',
        qrCodeCountDown: 'Active for {time:string} min',
        resetQRCode: 'Reset QR code',
        scanQRCode: 'Login with QR code',
        subtitle: 'Welcome back',
      },
      medicalUnitInvitation: {
        accept: 'Accept',
        additionalRoles: 'Additional roles',
        invitedToMedicalUnit: "You've been invited to join a site",
        reject: 'Reject',
      },
      mfaConfirmation: {
        infoPopUp: {
          paragraph1:
            'You can log in without SMS verification on an approved device.',
          paragraph2:
            'The approval is active for 30 days and needs to be renewed thereafter.',
          title: 'Approve this device',
        },
        inputs: {
          approveDevice: {
            label: 'Approve this device for login (30 days)',
          },
          verificationCode: {
            label: 'Verification code',
            validation: {
              invalidCode: 'Invalid code',
            },
          },
        },
        paragraph1: 'Please check your mobile phone',
        paragraph2: 'and enter the code sent by sms:',
        resendText: 'Send a new SMS',
        submitText: 'Verify code',
        editText: 'Edit phone number',
        title: 'Confirm your identity',
        noSmsTitle: 'You did not get SMS with verification code?',
        noSmsAction:
          'Check if the phone number is correct and try sending new SMS with verification code.',
      },
      mfaEnrollment: {
        paragraph1: 'We need to verify your identity.',
        paragraph2:
          'We will send a 6-digit code to your phone number for verification.',
        submitText: 'Confirm phone number',
        title: 'Add your phone number',
      },
      pickMedicalUnit: {
        subtitle: 'Select the site you want to access',
        successMessage: 'Switched to site {medicalUnitName:string}',
        noAccessMessage:
          'You do not have access to any sites with the account {username:string}. Please contact your local superuser or write Melatech support at support@melatech.io',
        title: 'Select site',
      },
      recovery: {
        checkYourMail:
          'Please check your mailbox and enter the code we sent you at',
        emailInstructions:
          'Enter your email address and we will send you instructions to reset your password.',
        submitCheckCode: 'Confirm identity',
        submitEmail: 'Send recovery code',
        title: 'Recover account',
      },
    },
    slowLoadingText: 'This is taking longer than usual.',
    signingOut: 'Logging out…',
    logout: 'Log out',
  },
  dataTable: {
    filters: 'Filters',
    clearAllFilters: 'Clear all',
    noActiveFilters: 'No active filters',
    removeFilter: 'Remove {category: string}:',
    searchOptions: 'Search',
    noMatchingOptions: 'No matches',
  },
  buttons: {
    add: 'Add',
    back: 'Back',
    cancel: 'Cancel',
    changeMedicalUnit: 'Change site',
    clear: 'Clear',
    close: 'Close',
    collapse: 'Collapse',
    completeEntry: 'Complete consult',
    confirm: 'Confirm',
    continue: 'Continue',
    delete: 'Delete',
    deleteAccount: 'Delete Account',
    edit: 'Edit',
    filter: 'Filter',
    help: 'Help',
    leave: 'Leave',
    patientOverview: 'Patient overview',
    remove: 'Remove',
    reset: 'Reset',
    restore: 'Restore',
    save: 'Save',
    saveAndContinue: 'Save and continue',
    saveChanges: 'Save changes',
    saveDraft: 'Save draft',
    saveDraftDisabled:
      'Only the draft creator can save or delete this draft. To close it yourself, use the green button to complete the case.',
    saveDraftDisabledAssistant:
      'Only the draft creator can save or delete this draft.',
    send: 'e-Sign and send',
    sendPathology: 'Send to pathologist',
    sendTeleReferral: 'Submit eConsult',
    signIn: 'Log in',
    signOut: 'Log out',
    read: 'Read',
    skip: 'Skip',
    tryAgain: 'Try again',
  },
  clinicalActions: {
    biopsy: 'Biopsy',
    biopsyAction: 'Biopsy type',
    biopsyActionText:
      '{biopsyTypeName|{Curretage: Curretage, Excision: Excision, Incision: Incision, Punch: Punch, Shave: Shave, *: Unspecified}} {text:string}',
    clinicalAction: 'Clinical action',
    dismissal: 'No further action',
    followUpAssessment: 'Follow-up',
    newImageRegistration: 'New image registration',
    noAction: 'No action',
    notSpecified: 'Not specified',
    physical: 'Physical',
    plannedBiopsy: 'Planned biopsy',
    referral: 'Referral',
    referralActionText:
      '{referralType|{physical: Physical referral, *: eConsult}}',
    tele: 'Tele',
    topicalTreatment: 'Topical treatment',
    noTreatment: 'No treatment',
    scraping: 'Scraping / swab',
    cryotherapy: 'Cryotherapy',
    oralTreatment: 'Oral treatment',
    other: 'Other',
  },
  dateDayMonthFormat: 'dd.MM',
  dateFormat: 'MM/dd/yyyy',
  dateHoursFormat: 'hh:mm a MM/dd/yyyy',
  hoursFormat: 'hh:mm a',
  dateText: {
    daysFromBaseline: '{dateDiff:number} day{{s}} from baseline',
    followUpMonths: '{months:number} month{{s}}',
    monthsFromBaseline: '{dateDiff:number} month{{s}} from baseline',
  },
  deleteUserRequest: {
    actionText: {
      cancel: 'Cancel',
      close: 'Close',
      delete: 'Delete',
    },
    impactNote:
      'Note that this will delete your account in <>Dermloop Learn<>, <>Dermloop Desktop<> and <>Dermloop Capture<>.',
    processNote:
      'Account deletion will be done manually by the Melatech team within a month.',
    regretInstruction:
      'If you regret your account deletion, sign into your account again before your account is deleted. You will be prompted if you wish to regret the account deletion.',
    requestPrompt: 'Do you want to request deletion of your account?',
    requestRegistered:
      'We have registered your account deletion request. We will delete your account <>within a month<>.',
    title: {
      received: 'Request received',
      requested: 'Delete account?',
    },
  },
  skinCheckRecency: {
    lessThanOneYear: 'Less than 1 year ago',
    oneToFiveYears: '1–5 years ago',
    moreThanFiveYears: 'More than 5 years ago',
    never: 'Never',
    unknown: "Don't know",
  },
  descriptors: {
    action: 'Action',
    addDifferentialDiagnosis: 'Add differential diagnosis',
    ageOfLesion: 'Age of lesion',
    ageOfRash: 'How long has the rash been present?',
    lastSkinCheck:
      'When did you last have your skin checked by a doctor or dermatologist?',
    plannedSkinCheckWithinSixMonths:
      'Did you plan to have your skin checked by a doctor or dermatologist within the next 6 months?',
    allLesions: 'All lesions',
    allConditions: 'All conditions',
    allUrgencies: 'All urgencies',
    archive: 'Archive',
    assessedBy: 'Assessed by',
    changesInAppearance: 'Changes in appearance',
    clinicalDiagnosis: 'Clinical diagnosis',
    clinicalDiagnosisFromLesion:
      'What is your tentative diagnosis for the lesion?',
    clinicalDiagnosisFromRash: 'What is your tentative rash diagnosis?',
    dateOfBirth: 'Date of birth',
    describeOther: 'Describe "Other"',
    describeTopicalTreatment: 'Describe topical treatment',
    differentialDiagnosis: 'Differential diagnosis',
    dob: 'DOB •',
    enterImmunosuppressantAndDate: 'Enter immunosuppressant and start/end date',
    enterMedication: 'Enter medication/ointment duration, dosage and frequency',
    enterTreatment: 'Enter prior treatment/evaluation and outcome',
    excisionSize: 'Excision size',
    familyHistoryOfMelanoma: 'Family history of melanoma',
    fetchingPatient: 'Fetching latest patient information',
    formerSkinCancer: 'Former skin cancer',
    formerTreatments: 'Former treatments',
    formerTreatmentsFromLesion:
      'Has the lesion undergone any prior treatment or evaluation?',
    formerTreatmentsFromRash:
      'Have any previous treatments been attempted for the rash? (oral or topical steroids, antihistamines, etc.)',
    formerTreatmentsOrTraumas: 'Former treatment{{nr:s}}',
    fromLesion: 'From lesion',
    hasAggravatingFactors:
      'Does anything worsen the rash? (cold, stress, etc.)',
    hasAlleviatingFactors:
      'Does anything alleviate the rash? (sun exposure, moisturizer, etc.)',
    hasGeneralSymptoms:
      'Has the patient experienced any new systemic symptoms alongside the rash? (fever, joint pain, etc.)',
    hasMedicalOrExposure:
      'Any new medications or exposures prior to the rash onset? (skincare/cosmetics, soap, detergents, etc.)',
    histologicalDiagnosis: 'Histological diagnosis',
    histopathologicalEvaluation: 'Histopathological evaluation',
    image: 'image',
    images: 'image(s)',
    immunosuppressantUse: 'Current/previous immunosuppressant use?',
    immunosuppressantUseExample:
      '(e.g. following organ/bone marrow transplant)',
    inspection: 'ABCD criteria',
    lastAccessed: 'Last accessed',
    lastClinicalAction: 'Last clinical action',
    lastRegistration: 'Last consult',
    condition: 'Condition',
    lesionDoesntMatch: "Lesion doesn't match patient",
    lesionImageBaseline: 'Baseline',
    conditions: 'Conditions',
    lesionThickness: 'Estimated thickness',
    loading: 'Loading',
    malignancy: 'Malignancy',
    medicalRecord: 'Medical record',
    noChanges: 'No changes',
    noLesion: 'No lesion',
    noPatient: 'No patient',
    noSymptoms: 'No symptoms',
    note: 'eConsult question and Case summary',
    eConsultNote: 'eConsult question',
    caseSummaryNote: 'Case summary',
    nrImages: '{nr:number} image{{s}}',
    nrMonths: '{nr:number} month{{s}}',
    objectiveFindings: 'Objective findings',
    optional: 'Optional',
    other: 'Other',
    otherAge: 'Other age',
    otherAggravatingFactors: 'Enter other things that worsen the rash',
    otherAlleviatingFactors: 'Enter other things that alleviate the rash',
    otherAppearanceChanges: 'Other appearance change(s)',
    otherDiscomforts: 'Other discomfort(s)',
    otherInspections: 'Other criteria',
    otherMedicationOrExposure:
      'Enter new medication and/or exposure, including duration',
    otherRashAge: 'Other rash age',
    otherSymptoms: 'Other symptom(s)',
    otherTreatmentsFromLesion: 'Enter prior treatment/evaluation and outcome',
    otherTreatmentsOrTrauma: 'Other treatments or trauma',
    pathologySpecimenID: 'Specimen ID',
    patientInfo: 'Patient info',
    pregnancyWithin6Months: 'Pregancy within last 6 months',
    rateDiagnosticsDifficulty: 'Rate diagnostic difficulty',
    reasonForConsult: 'What is the reason for this eConsult?',
    recentChanges:
      "Any recent changes in the lesion's size, shape, color, texture?",
    referrer: 'Referrer',
    reportedByPatient: 'Reported by patient',
    rescanPathologyID: 'Rescan specimen ID',
    restoreAccount: 'Restore account?',
    riskFactors: 'Risk factors',
    routine: 'Routine',
    scanSpecimentIDNumber: 'Scan specimen ID',
    selectAggravatingFactors: 'Things that worsen the rash',
    selectAlleviatingFactors: 'Things that alleviate the rash',
    selectChanges: 'Select change(s)',
    selectObjectiveFindings: 'Select objective finding(s)',
    selectRelevant: 'Select relevant',
    selectRelevantChanges: 'Select the relevant changes',
    selectRiskFactors: 'Select risk factor(s)',
    selectSkinTypeMatch: 'Select the skin type that best matches',
    selectSymptoms: 'Select symptom(s)',
    sinceWhen: 'Since when?',
    skinType: 'Skin type',
    SSN: 'CPR',
    symptom: 'Symptom',
    symptoms: 'Symptoms',
    symptomsFromLesion:
      'Are there any symptoms from the lesion? (itching, pain, bleeding, etc.)',
    symptomsFromRash:
      'Is the rash itchy, painful or causing any other discomfort?',
    teleEvaluation: 'Teledermatological evaluation',
    tentativeClinicalDiagnosis: 'Tentative clinical diagnosis',
    tentativeDiagnosis: 'Tentative diagnosis',
    typeOfCancer: 'Type of cancer',
    typeOfTeledermatology: 'Type of teledermatology',
    typeOfTreatmentOrTrauma: 'Type of treatment',
    uniqueIdentificationNumber: '{uniqueIdentificationNumber:string}',
    urgent: 'Urgent',
    whenChangesBegin: 'When did these changes in the lesion begin?',
    yesSelectChanges: 'Yes - Select changes',
    yesSelectSymptoms: 'Yes - Select symptom(s)',
    yo: 'yo',
  },
  diagnosis: {
    carcinoma: 'Carcinoma',
    melanoma: 'Melanoma',
  },
  errorMessages: {
    anErrorHappened: 'An error happened',
    badRequest: 'Bad request',
    cprLookupDailyLimitReached:
      "You have reached today's limit of CPR lookups. Try again tomorrow, or contact support if you need more.",
    cprLookupRequiresMitId:
      'Looking up a CPR number requires logging in with MitID. Log out and log in again with MitID.',
    errorOccurredWhileUpdating: 'An error occurred while updating',
    errorLoadingAvatar: 'Error loading avatar',
    invalidId: 'Not a valid identification number (CPR)',
    newHealthRecord: 'An error happened while creating new patient',
    newPatient: 'An error occurred while creating new patient',
    patientConsent: 'An error occurred while updating patient consent',
    somthingWentWrongPleaseContact:
      'Something went wrong. We are not entirely sure what happened - but please try to restart the app. If the issue persists, please contact',
    thisIsEmbarassing: 'This is embarassing',
    unauthorized: 'Unauthorized',
  },
  fieldPlaceholders: {
    enterAdditionalDetails: 'Enter additional details here...',
    enterAnswer: 'Enter your answer here...',
    enterNote: 'Enter your note here...',
    enterOtherAgeOfLesion: 'Enter description of the lesion age here...',
    enterOtherObjectiveFindingsHere: 'Enter other objective finding(s) here...',
    enterOtherRiskFactorsHere: 'Enter other risk factor(s) here...',
    findADiagnosis: 'Find a diagnosis',
    socialSecurityDummy: 'XXXXXX-XXXX',
    typeHereToEnterDescription: 'Enter description here...',
    typeHereToEnterOtherAge: 'Enter other age here...',
    typeHereToEnterOtherChanges: 'Enter other change(s) here...',
    typeHereToEnterOtherInspections: 'Enter other criteria here...',
    typeHereToEnterOtherSymptoms: 'Type here to enter other symptom(s)...',
    typeHereToEnterOtherTreatmentsOrTrauma: 'Enter other treatment(s)...',
  },
  formOptions: {
    imageLocation: {
      closeUpImage: 'Close-up image',
      dermoscopicImage: 'Dermoscopic image',
      location: 'Location (3D avatar)',
      overviewImage: 'Overview image',
    },
    reasonForConsult: {
      diagnosis: 'Help with the diagnosis',
      diagnosisAndManagement: 'Both',
      management: 'Help regarding the most appropriate management',
    },
  },
  genders: {
    female: 'Female',
    gender: 'Gender',
    male: 'Male',
    other: 'Other',
  },
  medicalRecord: {
    emptyPlaceholder: 'Completed registrations will be displayed here',
    error: {
      editingMedicalRecord: 'Error editing medical record',
    },
    fieldName: {
      aggravatingFactor: 'Aggravating factor{{nr:s}}',
      alleviatingFactor: 'Alleviating factor{{nr:s}}',
      assessmentDifficulty: 'Assessment difficulty',
      breslowThickness: 'Breslow thickness',
      changesInAppearance: 'Change{{nr:s}} in appearance',
      changesMadeBy: 'Changes made by',
      clinicalAction: 'Clinical action',
      clinicalDiagnosis: 'Clinical diagnosis',
      clinicalQuestion: 'Clinical question',
      contactedBy: 'Reviewed by',
      diagnosis: 'Diagnosis',
      pathologyID: 'Specimen ID',
      differentialDiagnosis: 'Differential diagnosis',
      disposition: 'Disposition',
      evaluatedBy: 'Evaluated and e-signed by',
      familyHistoryOfMelanoma: 'Family history of melanoma',
      generalSymptom: 'General symptom{{nr:s}}',
      hasFormerSkinCancer: 'Former malignant melanoma or skin cancer',
      imageQuality: 'Image quality',
      immunosuppressantUse: 'Immunosuppressant use',
      lesionAge: 'Lesion age',
      lesionThickness: 'Estimated thickness',
      medicationOrExposure: 'Medication or exposure',
      note: 'eConsult question and Case summary',
      patientResponseNote: 'Patient response note',
      pcpAssessmentReviewNote: 'Note',
      teleAssessmentNote: 'Note',
      otherObjectiveFindings:
        'Objective findings (e.g., sun damage, multiple/specific types of nevi)',
      pregnancyWithin6Months: 'Pregancy within last 6 months',
      previousTreatmentOrTrauma: 'Previous treatment or trauma',
      primaryDiagnosis: 'Primary diagnosis',
      rashAge: 'Rash age',
      recommendedClinicalAction: 'Recommended clinical action',
      registeredBy: 'Registered',
      created: 'Created',
      riskFactors:
        'Risk factors (e.g., sun exposure, immunosuppresive therapy, family history)',
      secondaryDiagnosis: 'Secondary diagnosis',
      skinType: 'Skin type',
      symptom: 'Symptom{{nr:s}}',
      tentativeClinicalDiagnosis: 'Tentative clinical diagnosis',
      tertiaryDiagnosis: 'Tertiary diagnosis',
      timeSpentReviewing: 'Time spent reviewing',
      followUpPriority: 'Follow-up priority',
      formAnswers: 'Form answers',
    },
    fieldValue: {
      age: '(time: {age:string})',
      appearanceChanges: '{triageInfo:string}',
      changedFromOldToNew: 'Changed from {old:string} to {new:string}',
      closeUpImage: 'Close-up image',
      dermoscopicImage: 'Dermoscopic image',
      none: 'None',
      notAskFurther: 'Not specified',
      notDisplayable: 'Could not be displayed',
      overviewImage: 'Overview image',
      photosChanged: '{removed:number} removed, {added:number} added',
      timeSpentReviewingMinutes: '{minutes:number} minute{{s}}',
      timeSpentIpcBillingParagraph:
        'I spent a total of {minutes:number} minute{{s}} on this IPC including review of patient data and records and documentation of my assessment. Consult was routed to referring provider.',
      unknownThickness: 'Tumor thickness unknown (Tx)',
    },
    urgencyDescriptions: {
      none: 'No recommendation for follow-up. Findings of no clinical significance or an already well-treated condition.',
      routine:
        'Follow-up recommended without urgency. E.g., benign changes or routine checks where urgent action is not necessary.',
      subAcute:
        'Suspicion of basal cell or squamous cell carcinoma, or another lesion where follow-up in the near term is considered necessary due to treatment needs.',
      acute:
        'Obvious malignant melanoma or another serious skin lesion where urgent evaluation is considered necessary.',
    },
    notRegistered: 'Not registered',
    subtitle: {
      anamnesis: 'Medical history',
      assessment: 'Assessment',
      baseline: 'Baseline',
      dermatologicalDiagnosis: 'New dermatological diagnosis',
      histopathologicalAssessment: 'Histopathological assessment',
      histoPathologicalDiagnosis: 'New histopathological diagnosis',
      objective: 'Objective',
      patientContacted: 'Specialist response reviewed',
      conditionTypeInfoEdited: '{type:string} info edited',
      teledermatologicalAssessment: 'Teledermatological assessment',
      teleAssessment: 'Tele-assessment',
      followUp: '{months:number} months follow-up',
      eConsult: 'eConsult',
    },
    title: {
      history: 'History',
      latestClinicalInformations: 'Latest clinical information',
      medicalRecord: 'Medical record',
      planAndConclusion: 'Plan and conclusion',
      predispositions: 'Predispositions',
      latestDiagnosis: 'Latest diagnosis',
      lesionInformation: 'Lesion information',
      rashInformation: 'Rash information',
      patientInformation: 'Patient information',
    },
    by: 'by',
  },
  medicalRecordTitle: {
    baseline: 'Baseline',
    histopatologicalAssessment: 'Histopathological assessment',
    patientContacted: 'Specialist response reviewed',
    conditionTypeInfoEdited: '{type:string} info edited',
    teledermatologicalAssessment: 'Teledermatological assessment',
    patientInfoEdited: 'Patient info edited',
    clinicalActionRemoved: '{action:string} removed',
    clinicalActionCancelled: '{action:string} cancelled',
  },
  medicalRecordValues: {
    age: '(time: {age:string})',
    aggravatingFactor: 'Aggravating factor{{nr:s}}',
    alleviatingFactor: 'Alleviating factor{{nr:s}}',
    anamnesis: 'Anamnesis',
    appearanceChanges: '{triageInfo:string}',
    assessmentDifficulty: 'Assessment difficulty',
    changedFromOldToNew: 'Changed from {old:string} to {new:string}',
    changesInAppearance: 'Change{{nr:s}} in appearance',
    changesMadeBy: 'Changes made by',
    clinicalAction: 'Clinical action',
    clinicalDiagnosis: 'Clinical diagnosis',
    closeUpImage: 'Close-up image',
    contactedBy: 'Reviewed by',
    dermoscopicImage: 'Dermoscopic image',
    differentialDiagnosis: 'Differential diagnosis',
    evaluatedBy: 'Evaluated and e-signed by',
    familyHistoryOfMelanoma: 'Family history of melanoma',
    generalSymptom: 'General symptom{{nr:s}}',
    hasFormerSkinCancer: 'Former malignant melanoma or skin cancer',
    history: 'History',
    imageQuality: 'Image quality',
    immunosuppressantUse: 'Immunosuppressant use',
    latestClinicalInformations: 'Latest clinical information',
    lesionAge: 'Lesion age',
    location: 'Location',
    medicalRecord: 'Medical record',
    medicationOrExposure: 'Medication or exposure',
    none: 'None',
    notAskFurther: 'Not specified',
    note: 'Note',
    notRegistered: 'Not registered',
    objective: 'Objective',
    otherObjectiveFindings: 'Other objective findings',
    overviewImage: 'Overview image',
    planAndConclusion: 'Plan and conclusion',
    predispositions: 'Predispositions',
    previousTreatmentOrTrauma: 'Previous treatment',
    primaryDiagnosis: 'Primary diagnosis',
    recommendedClinicalAction: 'Recommended clinical action',
    riskFactors: 'Risk factors',
    secondaryDiagnosis: 'Secondary diagnosis',
    skinType: 'Skin type',
    symptom: 'Symptom{{nr:s}}',
    tentativeClinicalDiagnosis: 'Tentative clinical diagnosis',
    tertiaryDiagnosis: 'Tertiary diagnosis',
    timeSpentReviewing: 'Time spent reviewing',
  },
  medicalUnitPicker: {
    noAccess: {
      logout: 'Log out',
      support: 'Melatech support:',
      title: 'Welcome to Dermloop!',
      gainAccess: 'To gain access, please contact your superuser.',
      sendReactivationEmailAction: 'Send reactivation email',
      superUserRestoreDescription:
        'To restore access, please contact your superuser(s):',
      selfReactivateRestoreDescription:
        'To restore access, please reactivate your account by clicking the button below.',
      userDeactivated: 'Your access (<>{email}<>) has been deactivated.',
      userHasNoAccess:
        'Your account (<>{email}<>) does not have access to any sites.',
      userInactive:
        'Your access (<>{email}<>) has been deactivated due to 90 days of inactivity.',
    },
  },
  numberInput: {
    rangeHint: 'Enter a whole number from {min:number} to {max:number}',
    minHint: 'Enter a whole number of at least {min:number}',
    maxHint: 'Enter a whole number of at most {max:number}',
    wholeHint: 'Enter a whole number',
  },
  patientInfo: {
    accNo: 'Acc. No.',
    cpr: 'CPR',
    phone: 'Tel.',
  },
  pendingInvitations: {
    alertBodyText:
      'Please open the invitation sent to your email and click the link to accept the invitation',
    alertClose: 'Close',
    alertTitle: 'You have pending invitations',
  },
  plaintext: {
    bad: 'Bad',
    baseline: 'Baseline',
    biopsyClinicalActionSelectionOnlyOnMobile:
      'Biopsy can be selected as clinical action<br/>ONLY from Dermloop Capture',
    changeIn: 'Change in',
    clinicalActionRequiresImages:
      'This action can only be selected once the required images have been captured.',
    contactMelatechMail: '{email:string}',
    contactSupport: 'Contact support:',
    usCountryCode: '+1',
    euCountryCode: '+45',
    usPhoneLink: 'tel:+18336902903',
    euPhoneLink: 'tel:+4544408245',
    usSupportNumber: '(833) 690-2903',
    euSupportNumber: '44 40 82 45',
    dateOfBirth: 'Date of birth',
    downloadImage: 'Download',
    enter: 'Enter',
    entered: 'Entered',
    formerTreatmentOrTrauma: 'Former treatment',
    good: 'Good',
    invalid: 'Invalid',
    mandatory: 'Mandatory',
    no: 'No',
    not: 'Not',
    nrFormerTreatmenstOrTraumas: '{nr:number} former treatment{{s}} selected',
    nrObjectiveFindings: '{nr:number} objective finding{{s}} selected',
    nrRiskFactors: '{nr:number} risk factor{{s}} selected',
    okay: 'Okay',
    or: 'Or',
    orSignIn: 'or log in using your username and password',
    overTheLast: 'over the last',
    requestDeletion: 'You have requested that we delete your account on',
    scanCode: 'Scan this code with your phone to confirm code:',
    scanned: 'Scanned',
    seeMore: 'See more',
    settings: 'Settings',
    sinceUnknow: 'since unknown',
    size: 'Size',
    unknown: 'Unknown',
    unspecified: 'Unspecified',
    while: 'while',
    years: 'Years',
    yes: 'Yes',
    yetBeenDeleted:
      'yet been deleted. You can therefore still restore your account with\nall data.',
    yourAccountHas: 'Your account has',
  },
  restoreUserRequest: {
    accountNotDeleted:
      'Your account has <>NOT<> yet been deleted. You can therefore still restore your account with all data.',
    actionText: {
      logOut: 'Log out',
      restore: 'Restore',
    },
    requestDate:
      'You have requested on <>{date:string}<> that we delete your account.',
    title: 'Restore account?',
  },
  roles: {
    admin: 'Dermloop Admin',
    editor: 'Editor',
    jurisdictionalGroupInsuranceManager: 'Insurance Manager',
    learnUser: 'Learn user',
    manager: 'Manager',
    medicalUnitManager: 'Superuser',
    medicalUnitParticipant: 'Clinician',
    medicalUnitAssistant: 'Medical Assistant',
    medicalUnitPathologist: 'Pathologist',
    medicalUnitTeledermatologist: 'Teledermatologist',
    medicalUnitTeledermatologistManager: 'Teledermatologist Manager',
    researcher: 'Researcher',
    reviewer: 'Reviewer',
    siteManager: 'Site Manager',
    trainingAssessmentExporter: 'Learn Data Exporter',
  },
  conditionTypes: {
    lesionExamples: 'Nevus, skin cancer, etc.',
  },
  statuses: {
    diagnosed: 'Diagnosed',
    draft: 'Draft',
    noAdditionalImages: 'No additional images',
    noRegisteredDiagnosis: 'No registered diagnosis',
    nrCloseUpImages: '{nr:number} close up image{{s}}',
    nrDermoscopicImages: '{nr:number} dermoscopic image{{s}}',
    nrOverviewImages: '{nr:number} overview image{{s}}',
    overdueFollowup: 'Overdue follow-up',
    pendingDiagnostic: 'Pending diagnosis',
    undiagnosable: 'Undiagnosable',
  },
  statusMessages: {
    invalidSSN: 'Invalid CPR',
    signedInAs: 'You are logged in as:',
    switchAccount: 'Switch account',
    unsavedChanges: 'Unsaved changes',
    welcomeBack: 'Welcome back',
  },
  tooltips: {
    selectMultiple: 'Select multiple',
  },
  email: {
    linkToDermloop: 'To Dermloop',
    unitBusinessHours: 'business hour{{hours:s}}',
    unitHours: 'hour{{hours:s}}',
    preReassignmentWarning: {
      titleOne: 'Case will be reassigned soon',
      titleMany: 'Cases will be reassigned soon',
      bodyOneSoon:
        'We have registered that you have 1 unfinished case. It will be reassigned in under {hours:number} {unit:string}. Click the button below to go to your cases.',
      bodyManySoon:
        'We have registered that you have {count:number} unfinished cases. The first will be reassigned in under {hours:number} {unit:string}. Click the button below to go to your cases.',
      bodyOneImminent:
        'We have registered that you have 1 unfinished case. It is being reassigned imminently. Click the button below to go to your cases.',
      bodyManyImminent:
        'We have registered that you have {count:number} unfinished cases. The first is being reassigned imminently. Click the button below to go to your cases.',
    },
    reassignmentAlert: {
      title:
        '{count:number} case{{count:s}} reassigned in {federationName:string}',
      body: '{count:number} case{{count:s}} ha{{count:s|ve}} been reassigned since {cutoff:string}.',
      sendersHeading: 'Previously assigned to:',
      senderCaseCount: '{count:number} case{{count:s}}',
      senderOverflow: 'and {count:number} other{{count:s}}',
      senderUnknown: 'Unknown sender',
    },
  },
  sms: {
    patientResponseReady:
      'Your response to your dermatological assessment on Dermloop is now ready. Click the link below to view your response. You will be asked to verify your identity with your phone number. The link is active for 30 days. If you have any questions, please contact NurseCare by phone on +45 70 20 22 32 or by email at info@nursecare.dk',
  },
  warningMessages: {
    delete: 'Delete',
    discard: 'Discard',
    leaveWithoutSaving: 'Leave Without Saving',
    leaveWithoutSavingWarning:
      'You are about to leave this page. Do you really want to leave without saving?',
  },
};

export default shared;
